/**
 * Бакшиш в „Закрий" и „Раздели и плати" — чистите правила (без React, без
 * заявки). Огледало на проверките на BE (`app/features/tips/`, договорът
 * CONTRACT §3): модалът не чака принтера, затова всичко, което BE би
 * отказал, трябва да се хване тук ПРЕДИ затварянето. BE остава авторитетът.
 *
 * Пари — само в цели центове (`utils/money.ts`). Бакшишът е ЕДИН и се плаща
 * изцяло с МЕТОДА НА СМЕТКАТА; „В брой" (1) носи само ресто, а „Смесено" (3)
 * никога не е метод за бакшиш — при тях бакшиш няма.
 */
import type { CloseBillTipIn } from "../../../../../api/bills";
import type { PaymentMethodPrintRoute } from "../../../../../api/paymentMethods";
import type { TipSettings } from "../../../../../api/tipSettings";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";

export const CASH_PAYMENT_METHOD_ID = 1;
export const CARD_PAYMENT_METHOD_ID = 2;
export const MIXED_PAYMENT_METHOD_ID = 3;
export const ON_ACCOUNT_PAYMENT_METHOD_ID = 4;

/** Кодовете на отказите — същите като на BE (текстовете: `closeTable.tip.errors.*`).
 *  `missing_permission` е 403-ката на BE (`Missing permission: <name>`) —
 *  текстът ѝ е `errors.missingPermission`, като преведения отговор. */
export type TipIssueCode =
  | "tip_negative"
  | "tip_disabled"
  | "tip_method_required"
  | "tip_method_mixed"
  | "tip_method_not_allowed"
  | "tip_over_max"
  | "tip_confirmation_required"
  | "tip_method_wrong_path"
  | "tip_method_not_on_printer"
  | "tip_on_account_not_allowed"
  | "tip_requires_items"
  | "missing_permission";

export interface TipIssue {
  code: TipIssueCode;
  /** Стойностите за текста — „12.50". */
  amount?: string;
  max?: string;
  threshold?: string;
  /** Името на метода (пътят на бакшиша). */
  method?: string;
  /** Правото от 403-ката (`payment_cash` / `payment_card`). */
  permission?: TipMethodPermission;
  /** Отказът е за бакшиша, ЗАПИСАН при закриването (повторен опит) — тогава
   *  се сменя само методът на сметката, не на бакшиша. */
  recorded?: true;
}

/** Правото за метода на бакшиша (BE §3 стъпка 5) — като за метода на сметката. */
export type TipMethodPermission = "payment_cash" | "payment_card";

/** Липсващото право за метода на бакшиша; `null` — няма какво да липсва
 *  (само „В брой" и „С карта" имат право). */
export function missingTipMethodPermission(
  methodId: number,
  permissions: { canPaymentCash: boolean; canPaymentCard: boolean },
): TipMethodPermission | null {
  if (methodId === CASH_PAYMENT_METHOD_ID && !permissions.canPaymentCash) {
    return "payment_cash";
  }
  if (methodId === CARD_PAYMENT_METHOD_ID && !permissions.canPaymentCard) {
    return "payment_card";
  }
  return null;
}

/**
 * Има ли сметката поне един ред, към който може да има бакшиш (BE §3
 * стъпка 7, `tip_requires_items`). Документът на сметката (BE и касата)
 * съдържа само несторнираните редове извън следите от разделяне, затова е
 * достатъчно да има ред. Без документ/`elements` не можем да преценим —
 * решава BE.
 */
export function billHasActiveRows(
  bill:
    | { elements?: ReadonlyArray<{ items?: ReadonlyArray<unknown> | null }> | null }
    | null
    | undefined,
): boolean {
  if (!bill || !Array.isArray(bill.elements)) return true;
  return bill.elements.some((element) => (element.items?.length ?? 0) > 0);
}

/** Бакшиш = „Общо с бакшиш" − поръчаното. Непопълнено поле → 0. */
export function tipCentsOf(
  orderedCents: number,
  totalWithTipCents: number | null,
): number {
  return totalWithTipCents === null ? 0 : totalWithTipCents - orderedCents;
}

// ── пътят (с или без касов бон) ─────────────────────────────────────────

/**
 * Минава ли плащане с метода покрай фискалния принтер — огледало на
 * `pos_only(pm)` на BE: „В брой" с `total: 0` („Закриване на сметка(1)")
 * или метод с `print_route === "pos"` (По сметка / „БЕЗ ФИСКАЛИЗИРАНЕ").
 */
export function isPosOnlyRoute(
  methodId: number,
  printRoute: PaymentMethodPrintRoute | undefined,
  sendsNonFiscalTotal: boolean,
): boolean {
  return (
    (methodId === CASH_PAYMENT_METHOD_ID && sendsNonFiscalTotal) ||
    printRoute === "pos"
  );
}

interface RouteCheckInput {
  tipMethodId: number;
  tipRoute: PaymentMethodPrintRoute | undefined;
  billMethodId: number;
  billRoute: PaymentMethodPrintRoute | undefined;
  /** Тялото ще носи `total: 0` (три клика или „По сметка"). */
  sendsNonFiscalTotal: boolean;
}

/**
 * Може ли бакшишът с този метод да мине по СЪЩИЯ път като плащането на
 * сметката (BE §3.6): и двата без бон, или и двата през бона — тогава
 * методът на бакшиша трябва да има истински слот на фискалното устройство.
 * Без `print_route` (по-стар отговор от огледалото) не можем да преценим —
 * решава BE/касата.
 */
export function tipRouteIssue({
  tipMethodId,
  tipRoute,
  billMethodId,
  billRoute,
  sendsNonFiscalTotal,
}: RouteCheckInput): "tip_method_wrong_path" | "tip_method_not_on_printer" | null {
  if (tipRoute === undefined) return null;
  const tipPosOnly = isPosOnlyRoute(tipMethodId, tipRoute, sendsNonFiscalTotal);
  const billPosOnly = isPosOnlyRoute(billMethodId, billRoute, sendsNonFiscalTotal);
  if (tipPosOnly !== billPosOnly) return "tip_method_wrong_path";
  if (!billPosOnly && tipRoute !== "fiscal") return "tip_method_not_on_printer";
  return null;
}

// ── при кой метод на сметката има бакшиш ────────────────────────────────

/**
 * Показва ли се бакшишът при този метод на сметката: методът е сред
 * избраните в настройките („Начини за плащане"), без „В брой" (там е само
 * рестото) и без „Смесено". Бакшишът се плаща със същия метод — отделен избор
 * за него няма.
 */
export function billMethodCarriesTip(
  billMethodId: number | null,
  methodsInSettings: readonly number[],
): boolean {
  return (
    billMethodId !== null &&
    billMethodId !== CASH_PAYMENT_METHOD_ID &&
    billMethodId !== MIXED_PAYMENT_METHOD_ID &&
    methodsInSettings.includes(billMethodId)
  );
}

export interface TipMethodContext {
  /** `/payment-methods/active` — методите на сесията (с `print_route`). */
  activeMethods: ReadonlyArray<{
    id: number;
    print_route?: PaymentMethodPrintRoute;
  }>;
  canPaymentCash: boolean;
  canPaymentCard: boolean;
  /** Клиентът на сметката може да плаща „По сметка" (4). */
  canOnAccount: boolean;
  cashOnlyMode: boolean;
  /** Методът на сметката; null — още не е избран. */
  billMethodId: number | null;
  /** `total: 0` в тялото (три клика или „По сметка"). */
  sendsNonFiscalTotal: boolean;
}

/**
 * Защо методът НЕ е допустим за бакшиша — с отказа, който BE би върнал за
 * него (§3): стъпка 3 — извън настройките/активните → `tip_method_not_allowed`;
 * стъпка 5 — правото (403) и „По сметка" без право на клиента; стъпка 6 —
 * пътят (`tip_method_wrong_path` / `tip_method_not_on_printer`). `null` —
 * допустим.
 */
export function tipMethodRefusal(
  tipMethodId: number,
  allowedInSettings: readonly number[],
  context: TipMethodContext,
  methodName: string,
): TipIssue | null {
  const routeOf = (id: number) =>
    context.activeMethods.find((method) => method.id === id)?.print_route;
  if (
    tipMethodId === MIXED_PAYMENT_METHOD_ID ||
    !allowedInSettings.includes(tipMethodId) ||
    !context.activeMethods.some((method) => method.id === tipMethodId)
  ) {
    return { code: "tip_method_not_allowed" };
  }
  const permission = missingTipMethodPermission(tipMethodId, context);
  if (permission) return { code: "missing_permission", permission };
  if (tipMethodId === ON_ACCOUNT_PAYMENT_METHOD_ID && !context.canOnAccount) {
    return { code: "tip_on_account_not_allowed" };
  }
  if (context.billMethodId !== null) {
    const routeCode = tipRouteIssue({
      tipMethodId,
      tipRoute: routeOf(tipMethodId),
      billMethodId: context.billMethodId,
      billRoute: routeOf(context.billMethodId),
      sendsNonFiscalTotal: context.sendsNonFiscalTotal,
    });
    if (routeCode) return { code: routeCode, method: methodName };
  }
  // „Закриване на сметка(1)": методите извън „В брой" са скрити (като тези
  // на сметката), дори пътят им да съвпада.
  if (context.cashOnlyMode && tipMethodId !== CASH_PAYMENT_METHOD_ID) {
    return { code: "tip_method_not_allowed" };
  }
  return null;
}

// ── проверките ───────────────────────────────────────────────────────────

/** Над прага за предупреждение ли е (тогава иска изрично потвърждение). */
export function needsTipConfirmation(
  tipCents: number,
  settings: Pick<TipSettings, "warnAboveAmount">,
): boolean {
  const thresholdCents = parseMoneyToCents(settings.warnAboveAmount);
  return thresholdCents !== null && tipCents > thresholdCents;
}

/** Отказът по самата СУМА (под поръчаното / над максимума) — показва се под
 *  нея, независимо от метода. */
export function tipAmountIssue(
  tipCents: number,
  settings: Pick<TipSettings, "maxAmount">,
): TipIssue | null {
  if (tipCents < 0) return { code: "tip_negative" };
  const maxCents = parseMoneyToCents(settings.maxAmount);
  if (maxCents !== null && tipCents > maxCents) {
    return {
      code: "tip_over_max",
      amount: formatCents(tipCents),
      max: formatCents(maxCents),
    };
  }
  return null;
}

export interface TipValidationInput {
  tipCents: number;
  settings: Pick<TipSettings, "enabled" | "maxAmount" | "warnAboveAmount">;
  tipMethodId: number | null;
  allowedMethodIds: readonly number[];
  /** Потвърдената сума (центове) — важи само за СЪЩАТА сума. */
  confirmedTipCents: number | null;
  /** Защо методът извън `allowedMethodIds` е отказан (`tipMethodRefusal`);
   *  без него — общото `tip_method_not_allowed`. */
  methodRefusal?: TipIssue | null;
  /** Сметката има поне един ред (`billHasActiveRows`); без него — да. */
  hasActiveRows?: boolean;
}

/** Отказите за метода, които BE дава СЛЕД максимума и потвърждението (§3
 *  стъпки 5–6); `tip_method_not_allowed` е преди тях (стъпка 3). */
const METHOD_ISSUES_AFTER_AMOUNT: ReadonlySet<TipIssueCode> = new Set<TipIssueCode>([
  "missing_permission",
  "tip_on_account_not_allowed",
  "tip_method_wrong_path",
  "tip_method_not_on_printer",
]);

/** Първият отказ в реда на BE (отрицателен → изключено → метод → максимум →
 *  потвърждение → право/път на метода → сметка без редове); `null` —
 *  бакшишът може да мине (или го няма). */
export function validateTip({
  tipCents,
  settings,
  tipMethodId,
  allowedMethodIds,
  confirmedTipCents,
  methodRefusal = null,
  hasActiveRows = true,
}: TipValidationInput): TipIssue | null {
  if (tipCents < 0) return { code: "tip_negative" };
  if (tipCents === 0) return null;
  const amount = formatCents(tipCents);
  if (!settings.enabled) return { code: "tip_disabled" };
  if (tipMethodId === null) return { code: "tip_method_required" };
  if (tipMethodId === MIXED_PAYMENT_METHOD_ID) return { code: "tip_method_mixed" };
  const methodIssue = allowedMethodIds.includes(tipMethodId)
    ? null
    : (methodRefusal ?? { code: "tip_method_not_allowed" });
  if (methodIssue && !METHOD_ISSUES_AFTER_AMOUNT.has(methodIssue.code)) {
    return methodIssue;
  }
  const maxCents = parseMoneyToCents(settings.maxAmount);
  if (maxCents !== null && tipCents > maxCents) {
    return { code: "tip_over_max", amount, max: formatCents(maxCents) };
  }
  if (needsTipConfirmation(tipCents, settings) && confirmedTipCents !== tipCents) {
    const thresholdCents = parseMoneyToCents(settings.warnAboveAmount) ?? 0;
    return {
      code: "tip_confirmation_required",
      amount,
      threshold: formatCents(thresholdCents),
    };
  }
  if (methodIssue) return methodIssue;
  if (!hasActiveRows) return { code: "tip_requires_items" };
  return null;
}

/**
 * Повторен опит: може ли ЗАПИСАНИЯТ бакшиш да излезе с (евентуално новия)
 * метод на сметката — огледало на BE `check_retry_tip`: изтрит метод →
 * `tip_method_not_allowed`; правото за метода на бакшиша (403); пътят спрямо
 * метода на сметката. Текущите настройки не се питат. Отказът носи
 * `recorded`, защото тук може да се смени само методът на сметката.
 */
export function recordedTipIssue({
  methodId,
  methodName,
  billMethodId,
  activeMethods,
  canPaymentCash,
  canPaymentCard,
  sendsNonFiscalTotal,
}: {
  /** Методът на записания бакшиш; null — изтрит (replay). */
  methodId: number | null;
  methodName: string;
  billMethodId: number | null;
  activeMethods: TipMethodContext["activeMethods"];
  canPaymentCash: boolean;
  canPaymentCard: boolean;
  sendsNonFiscalTotal: boolean;
}): TipIssue | null {
  if (methodId === null) return { code: "tip_method_not_allowed", recorded: true };
  const permission = missingTipMethodPermission(methodId, {
    canPaymentCash,
    canPaymentCard,
  });
  if (permission) return { code: "missing_permission", permission, recorded: true };
  if (billMethodId === null) return null;
  const routeOf = (id: number) =>
    activeMethods.find((method) => method.id === id)?.print_route;
  const routeCode = tipRouteIssue({
    tipMethodId: methodId,
    tipRoute: routeOf(methodId),
    billMethodId,
    billRoute: routeOf(billMethodId),
    sendsNonFiscalTotal,
  });
  return routeCode ? { code: routeCode, method: methodName, recorded: true } : null;
}

// ── парите на касата и тялото на заявката ────────────────────────────────

/**
 * Полетата на бакшиша за тялото на заявката — `undefined` без бакшиш (тогава
 * тялото е буквално старото). `totalWithTip` = прегледаното поръчано +
 * бакшишът; потвърдената сума — само над прага.
 */
export function buildTipRequest({
  tipCents,
  tipMethodId,
  reviewedOrderedCents,
  settings,
  includeTotalWithTip = true,
}: {
  tipCents: number;
  tipMethodId: number | null;
  reviewedOrderedCents: number;
  settings: Pick<TipSettings, "warnAboveAmount">;
  includeTotalWithTip?: boolean;
}): CloseBillTipIn | undefined {
  if (tipCents <= 0 || tipMethodId === null) return undefined;
  const request: CloseBillTipIn = {
    tipAmount: formatCents(tipCents),
    tipPaymentMethodId: tipMethodId,
  };
  if (includeTotalWithTip) {
    request.totalWithTip = formatCents(reviewedOrderedCents + tipCents);
  }
  if (needsTipConfirmation(tipCents, settings)) {
    request.tipConfirmedAmount = formatCents(tipCents);
  }
  return request;
}
